import React from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useLineActionsCatalogue } from '@/client/api/whatsapp/line-actions';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { getApiErrorField } from '@/client/utils/errors';
import { Button, type ButtonProps } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/** Readable text for any error-ish value (Evolution bodies come in many shapes). */
export const errText = (e: unknown): string => {
  if (e == null) return '';
  if (typeof e === 'string') return e;
  if (Array.isArray(e)) return e.map(errText).filter(Boolean).join('; ');
  if (typeof e === 'object') {
    const o = e as Record<string, unknown>;
    const inner = o.response ?? o.message ?? o.detail ?? o.error;
    if (inner != null && inner !== e) return errText(inner);
    try {
      return JSON.stringify(e);
    } catch {
      return String(e);
    }
  }
  return String(e);
};

/**
 * Message for an action error. The backend refuses some sends on purpose:
 * `opted_out` (403) and sandbox suppression get a plain explanation.
 */
export const actionError = (error: unknown, fallback: string): string => {
  const code = getApiErrorField<string>(error, 'error');
  if (code === 'opted_out') return 'Not sent: this number has opted out of WhatsApp messages.';
  if (code === 'suppressed' || getApiErrorField<unknown>(error, 'suppressed')) {
    return 'Not sent: the comms sandbox is on, so outbound WhatsApp is suppressed in this environment.';
  }
  const detail = getApiErrorField<unknown>(error, 'detail');
  return errText(detail) || (error as Error)?.message || fallback;
};

export const toastError = (error: unknown, fallback: string) => toast.error(actionError(error, fallback));

// ---------------------------------------------------------------------------
// permission gating from the backend catalogue

/** `can(action)`: the action exists on the server and the user holds its permission. */
export const useActionAccess = () => {
  const cat = useLineActionsCatalogue();
  const view = useGate(PERMISSIONS.whatsapp.view);
  const send = useGate(PERMISSIONS.whatsapp.send);
  const manage = useGate(PERMISSIONS.whatsapp.manageNumbers);
  const defs = cat.data?.data;
  const held = (perm: string) =>
    perm === PERMISSIONS.whatsapp.view ? view.allowed : perm === PERMISSIONS.whatsapp.send ? send.allowed : perm === PERMISSIONS.whatsapp.manageNumbers ? manage.allowed : false;
  return {
    loading: cat.isLoading,
    failed: cat.isError,
    can: (action: string) => {
      const d = defs?.find((x) => x.action === action);
      return !!d && held(d.perm);
    },
    why: (action: string) => {
      const d = defs?.find((x) => x.action === action);
      if (cat.isLoading) return 'Loading permissions...';
      if (!d) return 'This action is not available on the server';
      return held(d.perm) ? '' : `Requires the ${d.perm} permission`;
    },
  };
};

/** A button that is disabled (with a reason) unless the user may run `action`. */
export const ActionButton: React.FC<ButtonProps & { action: string }> = ({ action, disabled, title, ...props }) => {
  const a = useActionAccess();
  const blocked = !a.can(action);
  return <Button {...props} disabled={disabled || blocked} title={blocked ? a.why(action) : title} />;
};

// ---------------------------------------------------------------------------
// small pieces

export const Loading: React.FC<{ what: string }> = ({ what }) => (
  <p className="flex items-center gap-2 text-sm text-gray-500">
    <Loader2 className="h-4 w-4 animate-spin" /> Loading {what}...
  </p>
);

export const ErrorNote: React.FC<{ error: unknown; what: string }> = ({ error, what }) => (
  <p className="text-sm text-red-600">
    Could not load {what}: {actionError(error, 'unknown error')}
  </p>
);

export const Empty: React.FC<{ title: string; hint: string }> = ({ title, hint }) => (
  <div className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center">
    <p className="text-sm font-medium text-gray-900">{title}</p>
    <p className="mt-1 text-xs text-gray-500">{hint}</p>
  </div>
);

export const Section: React.FC<{ title: string; children: React.ReactNode; right?: React.ReactNode }> = ({ title, children, right }) => (
  <section className="space-y-3">
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {right}
    </div>
    {children}
  </section>
);

export const selectCls =
  'h-9 w-full rounded-md border border-input bg-background px-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60';
export const areaCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60';

export const ConfirmDialog: React.FC<{
  open: boolean;
  title: string;
  body: React.ReactNode;
  action: string;
  pending?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ open, title, body, action, pending, onConfirm, onClose }) => (
  <AlertDialog open={open} onOpenChange={(o) => !o && !pending && onClose()}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{body}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          disabled={pending}
          onClick={(e) => {
            e.preventDefault();
            onConfirm();
          }}
        >
          {pending ? 'Working...' : action}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// ---------------------------------------------------------------------------
// jid helpers

export const isGroupJid = (j?: string) => !!j && j.endsWith('@g.us');
export const jidUser = (j?: string) => (j ? j.split('@')[0].split(':')[0] : '');
export const digitsOf = (s: string) => s.replace(/\D/g, '');
export const phoneLabel = (j?: string) => (isGroupJid(j) ? j : jidUser(j) ? `+${jidUser(j)}` : '');
/** Recipient string for Evolution: a group jid as is, a phone as digits. */
export const recipientOf = (s: string) => (isGroupJid(s.trim()) ? s.trim() : digitsOf(s));

export const copyText = async (text: string, what: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error('Could not copy. Select the text and copy it by hand.');
  }
};

export const asArray = <T = any>(v: unknown): T[] => { // eslint-disable-line @typescript-eslint/no-explicit-any
  if (Array.isArray(v)) return v as T[];
  const o = v as { records?: unknown; groups?: unknown; chats?: unknown; data?: unknown } | null;
  for (const k of ['records', 'groups', 'chats', 'data'] as const) if (Array.isArray(o?.[k])) return o![k] as T[];
  return [];
};

export type Tab = { id: string; label: string };
export const TabBar: React.FC<{ tabs: Tab[]; value: string; onChange: (id: string) => void }> = ({ tabs, value, onChange }) => (
  <div className="flex gap-1 overflow-x-auto border-b px-4">
    {tabs.map((t) => (
      <button
        key={t.id}
        type="button"
        onClick={() => onChange(t.id)}
        className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium -mb-px ${
          value === t.id ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-800'
        }`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

/** File -> data URL body for upload fields (pictures). */
export const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('Could not read the file'));
    r.readAsDataURL(file);
  });
